import { useState, useEffect, useCallback } from 'react';
import {
  collection, onSnapshot, doc, updateDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { sha512 } from 'js-sha512';
import { db, addAuditLog, getSecondaryAuth } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import ToastNotification from '../../../components/ToastNotification';
import styles from './UsersSection.module.css';

function generatePassword() {
  const upper  = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower  = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%';
  const all = upper + lower + digits + special;
  const rand = (set) => set[Math.floor(Math.random() * set.length)];
  const chars = [rand(upper), rand(lower), rand(digits), rand(special)];
  for (let i = 0; i < 6; i++) chars.push(rand(all));
  return chars.sort(() => Math.random() - 0.5).join('');
}

function CredentialsModal({ name, email, password, onClose }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Account Created</h3>
        <p style={{ marginBottom: '1rem', color: '#4b5563', fontSize: '0.9rem' }}>
          Share these credentials with <strong>{name}</strong>. The password cannot be retrieved after closing this window.
        </p>
        <div className={styles.credentialsBox}>
          <div className={styles.credentialRow}>
            <span className={styles.credentialLabel}>Email</span>
            <span className={styles.credentialValue}>{email}</span>
          </div>
          <div className={styles.credentialRow}>
            <span className={styles.credentialLabel}>Password</span>
            <span className={styles.credentialValue}>{password}</span>
          </div>
        </div>
        <div className={styles.modalActions}>
          <button className={styles.cancelBtn} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy Credentials'}
          </button>
          <button className={styles.saveBtn} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

const ROLE_OPTIONS   = ['Admin', 'Supplier', 'Branch'];
const STATUS_OPTIONS = ['Active', 'Inactive'];

function initials(name) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

function avatarClass(role) {
  if (role === 'Admin')    return styles.avatarAdmin;
  if (role === 'Supplier') return styles.avatarSupplier;
  return styles.avatarBranch;
}

function StatusBadge({ status }) {
  return (
    <span className={`${styles.badge} ${status === 'Active' ? styles.badgeActive : styles.badgeInactive}`}>
      {status}
    </span>
  );
}

function EditUserModal({ user: target, onSave, onCancel }) {
  const [form, setForm] = useState({ ...target });

  function set(field, value) { setForm(prev => ({ ...prev, [field]: value })); }

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.entity.trim()) return;
    onSave(form);
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Edit User</h3>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Full Name</span>
            <input className={styles.input} value={form.name} onChange={e => set('name', e.target.value)} required />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Role</span>
            <select className={styles.input} value={form.role} onChange={e => set('role', e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Branch / Company</span>
            <input className={styles.input} value={form.entity} onChange={e => set('entity', e.target.value)} required />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Status</span>
            <select className={styles.input} value={form.status} onChange={e => set('status', e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="submit" className={styles.saveBtn}>Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddUserModal({ onSave, onCancel }) {
  const [form, setForm]   = useState({ name: '', email: '', role: 'Branch', entity: '' });
  const [errors, setErrors] = useState({});

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim())   errs.name   = 'Required';
    if (!form.email.trim())  errs.email  = 'Required';
    if (!form.entity.trim()) errs.entity = 'Required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave(form);
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Add New User</h3>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Full Name</span>
            <input
              className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Juan Dela Cruz"
            />
            {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Email</span>
            <input
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              type="email"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="user@topjuan.com"
            />
            {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Role</span>
            <select className={styles.input} value={form.role} onChange={e => set('role', e.target.value)}>
              {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Branch / Company</span>
            <input
              className={`${styles.input} ${errors.entity ? styles.inputError : ''}`}
              value={form.entity}
              onChange={e => set('entity', e.target.value)}
              placeholder="e.g. Branch Fairview"
            />
            {errors.entity && <span className={styles.fieldError}>{errors.entity}</span>}
          </label>
          <div className={styles.modalActions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>Cancel</button>
            <button type="submit" className={styles.saveBtn}>Add User</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function UsersSection() {
  const { user: currentUser }              = useAuth();
  const [users, setUsers]                  = useState([]);
  const [search, setSearch]                = useState('');
  const [editTarget, setEditTarget]        = useState(null);
  const [showAdd, setShowAdd]              = useState(false);
  const [newCredentials, setNewCredentials] = useState(null);
  const [toast, setToast]                  = useState(null);
  const dismissToast                       = useCallback(() => setToast(null), []);

  useEffect(() => {
    return onSnapshot(collection(db, 'users'), snap => {
      setUsers(snap.docs.map(d => ({ _docId: d.id, ...d.data() })));
    });
  }, []);

  const filtered = users.filter(u =>
    !search ||
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u._docId.toLowerCase().includes(search.toLowerCase()) ||
    u.entity?.toLowerCase().includes(search.toLowerCase())
  );

  async function handleEdit(updated) {
    try {
      await updateDoc(doc(db, 'users', updated._docId), {
        name:   updated.name,
        role:   updated.role,
        entity: updated.entity,
        status: updated.status,
      });
      await addAuditLog(currentUser.uid, currentUser.name, `Updated user ${updated.name}`, 'Maintenance');
      setEditTarget(null);
      setToast({ message: 'User updated.', type: 'success' });
    } catch (e) {
      console.error('Edit user failed:', e);
      setToast({ message: 'Failed to update user.', type: 'error' });
    }
  }

  async function handleToggle(u) {
    const next = u.status === 'Active' ? 'Inactive' : 'Active';
    try {
      await updateDoc(doc(db, 'users', u._docId), { status: next });
      await addAuditLog(currentUser.uid, currentUser.name, `${next === 'Active' ? 'Activated' : 'Deactivated'} user ${u.name}`, 'Maintenance');
      setToast({ message: `User ${next === 'Active' ? 'activated' : 'deactivated'}.`, type: 'success' });
    } catch (e) {
      console.error('Toggle status failed:', e);
    }
  }

  async function handleAdd(form) {
    const password = generatePassword();
    const hashedPassword = sha512(password);
    try {
      const secondaryAuth = getSecondaryAuth();
      const { user: newFirebaseUser } = await createUserWithEmailAndPassword(
        secondaryAuth,
        form.email.trim(),
        hashedPassword,
      );
      await secondaryAuth.signOut();

      await setDoc(doc(db, 'users', newFirebaseUser.uid), {
        name:      form.name.trim(),
        email:     form.email.trim(),
        role:      form.role.toLowerCase(),
        entity:    form.entity.trim(),
        initials:  initials(form.name),
        status:    'Active',
        createdAt: serverTimestamp(),
      });

      await addAuditLog(currentUser.uid, currentUser.name, `Added user ${form.name}`, 'Maintenance');
      setShowAdd(false);
      setNewCredentials({ name: form.name.trim(), email: form.email.trim(), password });
    } catch (e) {
      console.error('Add user failed:', e);
      const msg = e.code === 'auth/email-already-in-use'
        ? 'That email is already registered.'
        : 'Failed to create account. Please try again.';
      setToast({ message: msg, type: 'error' });
    }
  }

  return (
    <div className={styles.section}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by name, ID, or branch…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className={styles.addBtn} onClick={() => setShowAdd(true)}>+ Add User</button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Avatar</th>
              <th>User ID</th>
              <th>Full Name</th>
              <th>Role</th>
              <th>Branch / Company</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  {users.length === 0 ? 'Loading users…' : 'No users match your search.'}
                </td>
              </tr>
            ) : (
              filtered.map((u, i) => (
                <tr key={u._docId} className={i % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td>
                    <div className={`${styles.avatar} ${avatarClass(u.role)}`}>
                      {u.initials || initials(u.name || '??')}
                    </div>
                  </td>
                  <td className={styles.userId}>{u._docId.slice(0, 8)}</td>
                  <td className={styles.userName}>{u.name}</td>
                  <td style={{ textTransform: 'capitalize' }}>{u.role}</td>
                  <td>{u.entity}</td>
                  <td><StatusBadge status={u.status} /></td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button className={styles.editBtn} onClick={() => setEditTarget(u)}>Edit</button>
                      <button
                        className={u.status === 'Active' ? styles.deactivateBtn : styles.activateBtn}
                        onClick={() => handleToggle(u)}
                      >
                        {u.status === 'Active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <EditUserModal
          user={editTarget}
          onSave={handleEdit}
          onCancel={() => setEditTarget(null)}
        />
      )}

      {showAdd && (
        <AddUserModal
          onSave={handleAdd}
          onCancel={() => setShowAdd(false)}
        />
      )}

      {newCredentials && (
        <CredentialsModal
          name={newCredentials.name}
          email={newCredentials.email}
          password={newCredentials.password}
          onClose={() => setNewCredentials(null)}
        />
      )}

      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          onDismiss={dismissToast}
        />
      )}
    </div>
  );
}
